import { IFrameEthereumProvider } from '@ledgerhq/iframe-provider';
import { providers } from 'ethers';
import { getAddress, hexValue } from 'ethers/lib/utils';
import { Connector, ConnectorNotFoundError, UserRejectedRequestError, ResourceUnavailableError, ChainNotConfiguredError, AddChainError, SwitchChainError } from 'wagmi';
import { normalizeChainId } from '@wagmi/core';

class IFrameEthereumConnector extends Connector {
  constructor() {
    super(...arguments);
    this.id = 'ledgerLive';
    this.name = 'Ledger Live';
    this.ready = true;

    this.onAccountsChanged = accounts => {
      console.log({
        length: accounts.length
      });
      if (accounts.length === 0) this.emit('disconnect');else this.emit('change', {
        account: getAddress(accounts[0])
      });
    };

    this.onChainChanged = chainId => {
      const id = normalizeChainId(chainId);
      const unsupported = this.isChainUnsupported(id);
      this.emit('change', {
        chain: {
          id,
          unsupported
        }
      });
    };

    this.onDisconnect = () => {
      this.emit('disconnect');
    };
  }

  async connect({
    chainId
  } = {}) {
    try {
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();

      if (provider.on) {
        provider.on('accountsChanged', this.onAccountsChanged);
        provider.on('chainChanged', this.onChainChanged);
      }

      this.emit('message', {
        type: 'connecting'
      });
      const account = await this.getAccount(); // Switch to chain if provided

      let id = await this.getChainId();
      let unsupported = this.isChainUnsupported(id);

      if (chainId && id !== chainId) {
        const chain = await this.switchChain(chainId);
        id = chain.id;
        unsupported = this.isChainUnsupported(id);
      }

      return {
        account,
        chain: {
          id,
          unsupported
        },
        provider
      };
    } catch (error) {
      if (this.isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      if (error.code === -32002) throw new ResourceUnavailableError(error);
      throw error;
    }
  }

  async disconnect() {
    const provider = await this.getProvider();
    if (!(provider != null && provider.removeListener)) return;
    provider.removeListener('accountsChanged', this.onAccountsChanged);
    provider.removeListener('chainChanged', this.onChainChanged);
  }

  async getAccount() {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    const accounts = await provider.send('eth_requestAccounts'); // return checksum address

    return getAddress(accounts[0]);
  }

  async getChainId() {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    return await provider.send('eth_chainId').then(normalizeChainId);
  }

  async getProvider() {
    if (!this.providerInstance) {
      this.providerInstance = new IFrameEthereumProvider(this.options);
    }

    return this.providerInstance;
  }

  async getSigner() {
    const [provider, account] = await Promise.all([this.getProvider(), this.getAccount()]);
    return new providers.Web3Provider(provider).getSigner(account);
  }

  async isAuthorized() {
    try {
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();
      const accounts = await provider.send('eth_accounts');
      const account = accounts[0];
      return !!account;
    } catch {
      return false;
    }
  }

  async switchChain(chainId) {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    const id = hexValue(chainId);

    try {
      var _this$chains$find;

      await provider.send('wallet_switchEthereumChain', [{
        chainId: id
      }]);
      return (_this$chains$find = this.chains.find(x => x.id === chainId)) != null ? _this$chains$find : {
        id: chainId,
        name: `Chain ${id}`,
        network: `${id}`,
        rpcUrls: {
          default: ''
        }
      };
    } catch (error) {
      var _error$data, _error$data$originalE;

      const chain = this.chains.find(x => x.id === chainId);
      if (!chain) throw new ChainNotConfiguredError(); // Indicates chain is not added to provider

      if (error.code === 4902 || // Unwrapping for MetaMask Mobile
      // https://github.com/MetaMask/metamask-mobile/issues/2944#issuecomment-976988719
      (error == null ? void 0 : (_error$data = error.data) == null ? void 0 : (_error$data$originalE = _error$data.originalError) == null ? void 0 : _error$data$originalE.code) === 4902) {
        try {
          var _chain$rpcUrls$public;

          await provider.send('wallet_addEthereumChain', [{
            chainId: id,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: [(_chain$rpcUrls$public = chain.rpcUrls.public) != null ? _chain$rpcUrls$public : chain.rpcUrls.default],
            blockExplorerUrls: this.getBlockExplorerUrls(chain)
          }]);
          return chain;
        } catch (addError) {
          if (this.isUserRejectedRequestError(addError)) throw new UserRejectedRequestError(error);
          throw new AddChainError();
        }
      }

      if (this.isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      throw new SwitchChainError(error);
    }
  }

  async watchAsset({
    address,
    decimals = 18,
    image,
    symbol
  }) {
    const provider = await this.getProvider();
    if (!provider) throw new ConnectorNotFoundError();
    return await provider.send('wallet_watchAsset', [{
      type: 'ERC20',
      options: {
        address,
        decimals,
        image,
        symbol
      }
    }]);
  }

  isUserRejectedRequestError(error) {
    return error.code === 4001;
  }

}

export { IFrameEthereumConnector };
//# sourceMappingURL=ledger-live-wagmi-connector.esm.js.map
